import { useState, useEffect } from 'react';
import { FiChevronDown, FiChevronUp, FiClock } from 'react-icons/fi';
import { contractApi } from '../../../services/contractApi';
import type { ContractTypeAuditEntry } from '../../../services/contractApi';

interface AuditHistoryPanelProps {
    contractTypeName: string;
}

const AuditHistoryPanel = ({ contractTypeName }: AuditHistoryPanelProps) => {
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [entries, setEntries] = useState<ContractTypeAuditEntry[]>([]);

    useEffect(() => {
        if (expanded && entries.length === 0) {
            loadAudit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    const loadAudit = async () => {
        setLoading(true);
        try {
            const data = await contractApi.getContractTypeAudit(contractTypeName);
            setEntries(data);
        } catch {
            // silent fail for audit panel
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const summarizeChanges = (entry: ContractTypeAuditEntry): string => {
        if (!entry.previousStructures) return 'Configuração inicial';
        const prevRoles = entry.previousStructures[0]?.roles?.length || 0;
        const newRoles = entry.newStructures[0]?.roles?.length || 0;
        if (newRoles > prevRoles) return `Adicionou ${newRoles - prevRoles} papel(éis)`;
        if (newRoles < prevRoles) return `Removeu ${prevRoles - newRoles} papel(éis)`;
        // Same count — check what changed
        const prevRoleNames = (entry.previousStructures[0]?.roles || []).map((r: any) => `${r.role}(${r.minCount},o${r.signingOrder || r.signing_order || 1})`).join(',');
        const newRoleNames = (entry.newStructures[0]?.roles || []).map((r: any) => `${r.role}(${r.minCount || r.min_count},o${r.signingOrder || r.signing_order || 1})`).join(',');
        if (prevRoleNames !== newRoleNames) return 'Alterou configuração (quantidade, ordem ou papéis)';
        return 'Alterou configuração de papéis';
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
            {/* Collapsible header */}
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors rounded-lg"
            >
                <div className="flex items-center gap-2">
                    <FiClock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">Histórico de Alterações</h3>
                </div>
                {expanded
                    ? <FiChevronUp className="w-5 h-5 text-gray-500" />
                    : <FiChevronDown className="w-5 h-5 text-gray-500" />
                }
            </button>

            {/* Content */}
            {expanded && (
                <div className="px-6 pb-4 border-t border-gray-200 dark:border-slate-700">
                    {loading && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">Carregando...</p>
                    )}

                    {!loading && entries.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">Nenhuma alteração registrada</p>
                    )}

                    {!loading && entries.length > 0 && (
                        <div className="space-y-3 mt-4">
                            {entries.map(entry => (
                                <div key={entry.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
                                    <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-500" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">{entry.userId}</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(entry.changedAt)}</span>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">{summarizeChanges(entry)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AuditHistoryPanel;
